/**
 * Index translation between what the timeline RENDERS and the raw ref arrays
 * in project.json (`scenes`, `audioTrack`).
 *
 * The timeline never renders a ref array one-to-one. Scene refs marked `alt`
 * are dropped before blocks are built; the variant filter hides every ref whose
 * `attributes` do not match the current args; an audio ref whose media has no
 * known duration renders no block at all. A rendered block therefore sits at a
 * position that is **not** its index in the raw array.
 *
 * Writing a rendered position straight back into the raw array is the defect
 * these helpers exist to prevent (DEV-670 follow-up): it moved, inserted and
 * deleted whatever ref happened to occupy that slot instead of the one the user
 * pointed at. On a 28-scene project filtered to 18 visible blocks, a drop meant
 * for position 26 landed on 16.
 *
 * Every block carries `refIndex` — its position in the RAW array — and these
 * two functions are the only place a rendered position turns back into one.
 */

export interface RefIndexedBlock {
	/** Position of the ref this block was built from, in the RAW (unfiltered) array. */
	refIndex: number;
}

/**
 * Where a newly dropped item must be spliced into the raw array, given the
 * position the drop indicator showed among the *rendered* blocks.
 *
 * Landing "before the block currently at `visibleIndex`" means landing before
 * that block's ref, which leaves any hidden refs in between on the side they
 * were already on. Dropping past the last rendered block appends.
 */
export function rawInsertIndex(
	blocks: readonly RefIndexedBlock[],
	visibleIndex: number,
	rawLength: number,
): number {
	if (visibleIndex <= 0) return blocks.length ? blocks[0].refIndex : rawLength;
	if (visibleIndex >= blocks.length) return rawLength;
	return blocks[visibleIndex].refIndex;
}

/**
 * The raw-array splice plan for a drag-reorder.
 *
 * `cdkCurrentIndex` is CDK's `currentIndex`, which is already expressed in the
 * rendered list **without** the dragged block — CDK lifts it out and lets a
 * placeholder follow the cursor — so it is resolved against that same list,
 * not against the one the user saw before grabbing. Reading it in the wrong
 * coordinate space is an off-by-one that only shows up when something is
 * hidden, which is exactly when it is hardest to spot.
 *
 * Returns a remove-then-insert pair rather than a single target index, because
 * `moveItemInArray`'s one index means "before" or "after" the element at it
 * depending on drag direction; splitting it keeps "land immediately before this
 * ref" true in both directions.
 */
export function planRawMove(
	blocks: readonly RefIndexedBlock[],
	draggedRefIndex: number,
	cdkCurrentIndex: number,
	rawLength: number,
): { removeAt: number; insertAt: number } {
	// A drag that puts the block back in its own visible slot must not touch the
	// raw array at all. Landing "before the next visible ref" would otherwise
	// carry it across the hidden refs in between — a reorder with no visible
	// cause, which is how the b/w twins in the journal ended up swapped.
	if (cdkCurrentIndex === blocks.findIndex(b => b.refIndex === draggedRefIndex)) {
		return { removeAt: draggedRefIndex, insertAt: draggedRefIndex };
	}

	const afterLift = blocks.filter(b => b.refIndex !== draggedRefIndex);
	const target = afterLift[cdkCurrentIndex];

	// No target → dropped past the last rendered block, so it belongs at the end
	// of the array the removal leaves behind.
	if (!target) return { removeAt: draggedRefIndex, insertAt: Math.max(rawLength - 1, 0) };

	// Removing the dragged ref shifts every later ref down by one.
	const insertAt = target.refIndex > draggedRefIndex ? target.refIndex - 1 : target.refIndex;
	return { removeAt: draggedRefIndex, insertAt };
}
